
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { getAuthHeader } from "@/lib/authHelpers";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";

interface ReportModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    postId?: string;
    photoId?: string;
    reviewId?: string;
}

export function ReportModal({ open, onOpenChange, postId, photoId, reviewId }: ReportModalProps) {
    const { t } = useI18n();
    const { toast } = useToast();
    const [reason, setReason] = useState<string>("");
    const [description, setDescription] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const REPORT_REASONS = [
        { id: "spam", label: t("report.reason.spam") },
        { id: "inappropriate", label: t("report.reason.inappropriate") },
        { id: "offensive", label: t("report.reason.offensive") },
        { id: "misleading", label: t("report.reason.misleading") },
        { id: "copyright", label: t("report.reason.copyright") },
        { id: "other", label: t("report.reason.other") }
    ];

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!reason) return;

        try {
            setIsSubmitting(true);
            const authHeader = await getAuthHeader();

            const response = await fetch("/api/reports", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(authHeader || {})
                },
                body: JSON.stringify({
                    postId,
                    photoId,
                    reviewId,
                    reason,
                    description: description || undefined
                })
            });

            if (response.ok) {
                const data = await response.json();
                toast({
                    title: t("report.thanksTitle"),
                    description: t("report.thanksDesc"),
                });
                onOpenChange(false);
                setReason("");
                setDescription("");
            } else {
                const error = await response.json();
                if (response.status === 409) {
                    toast({
                        title: t("report.alreadyReportedTitle"),
                        description: t("report.alreadyReportedDesc"),
                        variant: "destructive"
                    });
                    onOpenChange(false);
                } else {
                    throw new Error(error.error || t("report.submitFailed"));
                }
            }
        } catch (error) {
            console.error("Error submitting report:", error);
            toast({
                title: t("report.errorTitle"),
                description: t("report.errorDesc"),
                variant: "destructive"
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-500" />
                        {t("report.title")}
                    </DialogTitle>
                    <DialogDescription>
                        {t("report.desc")}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    <RadioGroup value={reason} onValueChange={setReason} className="space-y-2">
                        {REPORT_REASONS.map((r) => (
                            <div key={r.id} className="flex items-center space-x-2">
                                <RadioGroupItem value={r.id} id={r.id} />
                                <Label htmlFor={r.id} className="cursor-pointer">{r.label}</Label>
                            </div>
                        ))}
                    </RadioGroup>

                    {reason && (
                        <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                            <Label htmlFor="description">{t("report.explanationOptional")}</Label>
                            <Textarea
                                id="description"
                                placeholder={t("report.extraContext")}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="resize-none"
                                rows={3}
                            />
                        </div>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                            {t("shops.cancel")}
                        </Button>
                        <Button type="submit" disabled={!reason || isSubmitting} variant="destructive">
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t("report.sending")}
                                </>
                            ) : (
                                t("report.submit")
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
